import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { FeatureFlags, PublicUserProjection } from '@kennisbank/shared';

/**
 * Cookie-session auth. The browser holds an httpOnly signed session cookie
 * (`kennisbank.sid`) that the backend manages. We never see or touch it
 * from JavaScript — every API request just uses `credentials: 'include'`
 * to attach the cookie automatically.
 *
 * In development a Bearer dev-token is still supported via the persona
 * picker on /login. We stash it in sessionStorage and append it as
 * Authorization header. The backend's authenticate middleware short-circuits
 * the bearer for known dev tokens; production rejects bearers entirely.
 */
const DEV_TOKEN_STORAGE_KEY = 'kennisbank.devToken';
const SESSIE_STORAGE_KEY = 'kennisbank.sessie';

/**
 * /api/me = de gebruikersprojectie plus het server-afgeleide features-object
 * (issue #38). `features` optioneel: een oudere backend zonder het veld laat
 * de sidebar gewoon alles tonen.
 */
export type MeUser = PublicUserProjection & {
  features?: FeatureFlags;
  demo?: boolean;
  /** Mag Beheer > Demo-toegang zien (server-allowlist, niet de rol). */
  demoToegangBeheer?: boolean;
  /** Verwijsbrief-functie aan voor dit e-mailadres (ADR 0017). */
  verwijsbrief?: boolean;
  /** Wisselknop SUPPOHRT <-> RHOGO: mag de gebruiker wisselen, en werkt hij nu in RHOGO. */
  tenantWissel?: { mag: boolean; actief: boolean; thuisTenantId?: string };
};

export interface AuthContextValue {
  user: MeUser | null;
  loading: boolean;
  source: 'cookie' | 'dev_token' | null;
  /** Set a dev token (or clear). Triggers /api/me re-fetch. */
  setDevToken: (token: string | null) => void;
  /** Sign out. POSTs to /api/v1/oauth/entra/logout, clears dev token, navigates to /login. */
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [devToken, setDevTokenState] = useState<string | null>(() =>
    typeof window === 'undefined' ? null : window.sessionStorage.getItem(DEV_TOKEN_STORAGE_KEY),
  );
  const [user, setUser] = useState<MeUser | null>(null);
  // Always start in loading=true so RequireAuth can wait for the first
  // /api/me round-trip before deciding to bounce to /login. Cookie-based
  // auth is opaque from the client — we only know we're signed in by
  // asking the backend.
  const [loading, setLoading] = useState<boolean>(true);

  const setDevToken = useCallback((token: string | null) => {
    if (token) {
      window.sessionStorage.setItem(DEV_TOKEN_STORAGE_KEY, token);
    } else {
      window.sessionStorage.removeItem(DEV_TOKEN_STORAGE_KEY);
    }
    setDevTokenState(token);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await fetch('/api/v1/oauth/entra/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } catch (err) {
      console.error('Logout request failed (cookie may persist server-side until expiry)', err);
    }
    setDevToken(null);
    setUser(null);
    // Hard navigation guarantees no stale React state survives the sign-out.
    window.location.assign('/login');
  }, [setDevToken]);

  // Resolve user via /api/me on mount + whenever devToken changes.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const headers: Record<string, string> = {};
        if (devToken) headers.Authorization = `Bearer ${devToken}`;
        const res = await fetch('/api/me', {
          credentials: 'include',
          headers,
        });
        if (cancelled) return;
        if (res.status === 401) {
          // Either no session cookie (normal pre-login) or dev token rejected.
          if (devToken) setDevToken(null);
          setUser(null);
          return;
        }
        if (res.status === 403) {
          // Het RHOGO-slot (ADR 0018) trekt een lopende sessie in met deze
          // fout. Zonder doorsturen zou de gebruiker een leeg inlogscherm zien
          // en blijven proberen; het inlogscherm heeft de uitleg al.
          const body = (await res.json().catch(() => null)) as { error?: string } | null;
          if (cancelled) return;
          setUser(null);
          if (
            body?.error === 'nog_niet_open' &&
            new URLSearchParams(window.location.search).get('error') !== 'nog_niet_open'
          ) {
            window.location.assign('/login?error=nog_niet_open');
          }
          return;
        }
        if (!res.ok) {
          console.error('Failed to load /api/me', res.status);
          setUser(null);
          return;
        }
        const json = (await res.json()) as MeUser;
        setUser(json);
      } catch (err) {
        if (cancelled) return;
        console.error('Network error loading /api/me', err);
        setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [devToken, setDevToken]);

  // Demo: een ingetrokken of verlopen toegangscode beëindigt de sessie
  // server-side. Zonder deze controle zou een open tabblad gewoon blijven staan
  // tot de bezoeker toevallig iets aanklikt; nu staat hij binnen een minuut op
  // het inlogscherm met uitleg.
  const isDemo = user?.demo === true;
  useEffect(() => {
    if (!isDemo) return;
    const timer = window.setInterval(() => {
      void fetch('/api/me', { credentials: 'include' })
        .then((res) => {
          if (res.status === 401) window.location.assign('/login?error=demo_access_ended');
        })
        .catch(() => undefined);
    }, 30_000);
    return () => window.clearInterval(timer);
  }, [isDemo]);

  // Eén browser = één sessiecookie. Logt een ander tabblad in met een ander
  // account of wisselt het naar RHOGO, dan pollde een open tabblad met de oude
  // rol door op de nieuwe sessie: elke minuut een access.refused, en die
  // regels werden valse incidentsignalen (29 okt 2026, 81x bij Linda). Het
  // storage-event vuurt alleen in de ándere tabbladen, en alleen bij een
  // gewijzigde waarde, dus twee tabbladen met dezelfde sessie herladen niet.
  const sessieSleutel = user ? `${user.id}|${user.tenantId}` : null;
  useEffect(() => {
    // Dev-tokens leven per tabblad (sessionStorage): twee persona's naast elkaar
    // zouden elkaar eindeloos herladen.
    if (!sessieSleutel || devToken) return;
    try {
      window.localStorage.setItem(SESSIE_STORAGE_KEY, sessieSleutel);
    } catch {
      // Geblokkeerde opslag: dan alleen geen herlaadsignaal naar andere tabbladen.
    }
    const opWissel = (e: StorageEvent): void => {
      if (e.key === SESSIE_STORAGE_KEY && e.newValue && e.newValue !== sessieSleutel) {
        window.location.reload();
      }
    };
    window.addEventListener('storage', opWissel);
    return () => window.removeEventListener('storage', opWissel);
  }, [sessieSleutel, devToken]);

  const source: AuthContextValue['source'] = user ? (devToken ? 'dev_token' : 'cookie') : null;

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, source, setDevToken, signOut }),
    [user, loading, source, setDevToken, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside an AuthProvider');
  return ctx;
}
